import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import paymentService from '../services/payment-service';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (email: string) => void;
  email: string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ isOpen, onClose, onSuccess, email }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [country, setCountry] = useState('Canada');
  const [postalCode, setPostalCode] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(1000); // $10 in cents

  // Stripe CardElement options - disable postal code since we handle it separately
  const cardElementOptions = {
    hidePostalCode: true, // Hide Stripe's postal code field
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  if (!isOpen) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.');
      return;
    }

    // Validate postal code based on country
    if (country === 'Canada' && !isValidCanadianPostalCode(postalCode)) {
      setError('Please enter a valid Canadian postal code (e.g., A1A 1A1)');
      return;
    }
    
    if (country === 'United States' && !isValidUSZipCode(postalCode)) {
      setError('Please enter a valid US ZIP code (e.g., 12345 or 12345-6789)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create payment intent
      const paymentResponse = await paymentService.createPaymentIntent({
        email,
        tier: 'supporter',
        country,
        postalCode: country === 'Canada' ? postalCode : undefined,
        promoCode: promoApplied ? promoCode : undefined,
        amount: finalAmount, // Pass discounted amount
      });

      if (!paymentResponse.success || !paymentResponse.clientSecret) {
        throw new Error(paymentResponse.error || 'Failed to create payment intent');
      }

      // Get payment method
      const cardElement = elements?.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const paymentMethodId = await paymentService.createPaymentMethod(cardElement);
      if (!paymentMethodId) {
        throw new Error('Failed to create payment method');
      }

      // Confirm payment
      const confirmResponse = await paymentService.confirmPayment(
        paymentResponse.clientSecret!,
        paymentMethodId!
      );

      if (!confirmResponse.success) {
        throw new Error(confirmResponse.error || 'Payment failed');
      }

      // Success!
      onSuccess(email);
      onClose();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const isValidCanadianPostalCode = (code: string): boolean => {
    const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
    return postalCodeRegex.test(code);
  };

  const isValidUSZipCode = (code: string): boolean => {
    const zipCodeRegex = /^\d{5}(-\d{4})?$/;
    return zipCodeRegex.test(code);
  };

  const formatCanadianPostalCode = (value: string): string => {
    // Remove all non-alphanumeric characters
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    
    // Format as A1A 1A1
    if (cleaned.length >= 6) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)}`;
    }
    return cleaned;
  };

  const formatUSZipCode = (value: string): string => {
    // Remove all non-numeric characters
    const cleaned = value.replace(/\D/g, '');
    
    // Format as 12345-6789 if we have 9 digits
    if (cleaned.length >= 9) {
      return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 9)}`;
    }
    // Just return the cleaned numbers for shorter inputs
    return cleaned;
  };

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCanadianPostalCode(e.target.value);
    setPostalCode(formatted);
  };

  const handleUSZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatUSZipCode(e.target.value);
    setPostalCode(formatted);
  };

  const handleApplyPromo = () => {
    const code = promoCode.toUpperCase().trim();

    // Check valid promo codes
    if (code === 'LAUNCH50') {
      setDiscount(50); // 50% off
      setFinalAmount(500); // $5 in cents
      setPromoApplied(true);
      setError('');
    } else if (code === '') {
      setError('Please enter a promo code');
    } else {
      setError('Invalid promo code');
      setPromoApplied(false);
      setDiscount(0);
      setFinalAmount(1000);
    }
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoApplied(false);
    setDiscount(0);
    setFinalAmount(1000);
    setError('');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="model-content" onClick={(e) => e.stopPropagation()}>
        <button className="payment-modal-close" onClick={onClose}>×</button>
        
        <div className="payment-header">
          <h2>Join the Supporters Club</h2>
          <p>One membership for calculators, bookkeeping, and scheduling: just $10/year</p>

          <div className="supporters-benefits">
            <h3>What You Get:</h3>
            <ul className="benefits-list">
              <li>✓ <strong>Unlimited saved scenarios</strong> (vs 10/week free)</li>
              <li>✓ <strong>Advanced calculator features</strong></li>
              <li>✓ <strong>Bookkeeping for up to 10 businesses</strong> (vs 1 free)</li>
              <li>✓ <strong>Scheduling Pro</strong>: hide branding, API keys, staff logins, payment collection</li>
              <li>✓ <strong>PDF export</strong> for advisors (coming soon)</li>
              <li>✓ <strong>Support Canadian financial literacy</strong></li>
              <li>✓ <strong>Help us grow</strong> and build better tools</li>
            </ul>
            <p className="honest-value">
              Your support helps us keep Larry's Help running and improving. All features unlock immediately - no waiting, no auto-renewal, no pressure.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="payment-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              readOnly
              className="readonly-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="country">Country</label>
            <select
              id="country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="country-select"
            >
              <option value="Canada">Canada</option>
              <option value="United States">United States</option>
            </select>
          </div>

          {country === 'Canada' && (
            <div className="form-group">
              <label htmlFor="postalCode">Postal Code:<br/>               <small className="format-hint">Format: A1A 1A1</small>
              </label>
              <input
                type="text"
                id="postalCode"
                value={postalCode}
                onChange={handlePostalCodeChange}
                placeholder="A1A 1A1"
                maxLength={7}
                className="postal-code-input"
              />
            </div>
          )}

          {country === 'United States' && (
            <div className="form-group">
              <label htmlFor="postalCode">ZIP Code:<br/> <small className="format-hint">Format: 12345</small></label>
              <input
                type="text"
                id="postalCode"
                value={postalCode}
                onChange={handleUSZipCodeChange}
                placeholder="12345"
                maxLength={10}
                className="postal-code-input"
              />
              
            </div>
          )}

          <div className="form-group">
            <label htmlFor="card">Card Details</label>
            <div className="card-element-container">
              <CardElement options={cardElementOptions} />
              <br/>
            </div>
          </div>

          <div className="form-group promo-code-group">
            <label htmlFor="promoCode">Promo Code (Optional)</label>
            <div className="promo-code-input-wrapper">
              <input
                type="text"
                id="promoCode"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Enter code"
                disabled={promoApplied}
                className="promo-code-input"
              />
              {!promoApplied ? (
                <button
                  type="button"
                  onClick={handleApplyPromo}
                  className="btn-apply-promo"
                >
                  Apply
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleRemovePromo}
                  className="btn-remove-promo"
                >
                  Remove
                </button>
              )}
            </div>
            {promoApplied && (
              <p className="promo-success">✓ Promo code "{promoCode}" applied! {discount}% off</p>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="payment-summary">
            <div className="summary-item">
              <span>Supporters Club (Annual)</span>
              <span>$10.00 CAD</span>
            </div>
            {promoApplied && discount > 0 && (
              <div className="summary-item discount-item">
                <span>Discount ({discount}% off)</span>
                <span>-${((1000 - finalAmount) / 100).toFixed(2)} CAD</span>
              </div>
            )}
            <div className="summary-total">
              <span>Total</span>
              <span>${(finalAmount / 100).toFixed(2)} CAD</span>
            </div>
          </div>

          <button 
            type="submit" 
            className="payment-submit-btn" 
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Join Supporters Club - $10/year'}
          </button>
        </form>
        <div className="payment-footer">
          <div className="payment-methods">
            <p className="payment-methods-label">We accept:</p>
            <div className="payment-icons">
              <span className="payment-icon">💳 Visa</span>
              <span className="payment-icon">💳 Mastercard</span>
              <span className="payment-icon">💳 Amex</span>
            </div>
          </div>
          <p>✅ Your payment is secure and encrypted by Stripe</p>
          <p>✅ You'll receive a receipt at {email}</p>
          <p><strong>✅ No auto-renewal - you stay in control</strong></p>
          <p className="trust-note" style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: '#666' }}>
            We'll remind you before your year expires, but you choose whether to renew. Your money, your choice.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal; 
